import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "common/locales";
import {
  createFarm,
  selectAllFarms,
  selectCurrentFarm,
  switchFarm,
  useAppDispatch,
  useAppSelector,
} from "model";
import { ChangeEvent, FC, MutableRefObject, useEffect, useState } from "react";
import { MapRef } from "react-map-gl";
import { Link, Switch, useHistory, useRouteMatch } from "react-router-dom";
import styled from "styled-components";

import { unwrapResult } from "@reduxjs/toolkit";
import { envelope, lineString } from "@turf/turf";
import { PrivateRoute } from "app";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import {
  DrawerForm,
  FullScreenDialog,
  InfoSnackbar,
  MobileTopHintContainer,
  VerticalCenterContainer,
} from "common/components";
import PostcodeLookupInput from "common/components/PostcodeLookupInput";
import { fetchIPLocation } from "model/geolocation";

interface FarmInputProps {
  mapRef: MutableRefObject<MapRef | null>;
}

export const FarmInput: FC<FarmInputProps> = ({ mapRef }) => {
  const [farmName, setFarmName] = useState<string>("");
  const [farmNameError, setFarmNameError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(true);
  const userId = useAppSelector((state) => state.user.details?.guid);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { path } = useRouteMatch();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { notify } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const userFarms = useAppSelector(selectAllFarms);
  const currentFarm = useAppSelector(selectCurrentFarm);
  useEffect(() => {
    // fetching ip data once on mount
    void dispatch(fetchIPLocation());
  }, []);
  let currentFarmGuid = ""; // should be always set later when we have at least 1 farm
  if (userFarms.length > 0) {
    currentFarmGuid = currentFarm ? currentFarm.guid : userFarms[0].guid;
  }

  const handleSave = async (): Promise<void> => {
    if (farmName === "") {
      setFarmNameError(true);
      return;
    }

    setIsLoading(true);

    const map = mapRef.current?.getMap();
    const farmGeom = envelope(lineString(map.getBounds().toArray()));

    if (farmGeom.geometry && userId) {
      try {
        const newFarm = await dispatch(
          createFarm({
            guid: "new",
            name: farmName,
            owner: userId,
            user: userId,
            geom: {
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  properties: {},
                  geometry: {
                    type: "MultiPolygon",
                    coordinates: [farmGeom.geometry?.coordinates],
                  },
                },
              ],
            },
            fields: [],
            animalGroups: [],
          })
        ).then(unwrapResult);

        notify(t("farmInput.add.success", { farmName }));

        // TODO: figure out why newFarm can be undefined; or this check necessary?
        if (newFarm) {
          void dispatch(switchFarm(newFarm.guid));
          mixpanel.track("FarmAdd successful");
          history.replace(`/farm/${newFarm.guid}`);
        } else {
          history.push("");
        }
      } catch (err) {
        mixpanel.track("FarmAdd failed");
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (farmName !== "") {
      setFarmNameError(false);
    }

    setFarmName(e.target.value);
  };

  if (isMobile) {
    return (
      <>
        <Switch>
          <PrivateRoute path={`${path}/enter-name`}>
            <FullScreenDialog>
              <DialogTitle>{t("farm.add.addName.mobile")}</DialogTitle>
              <DialogContent>
                <VerticalCenterContainer>
                  <Container>
                    <DrawerForm>
                      <TextField
                        variant="standard"
                        required
                        value={farmName}
                        error={farmNameError}
                        helperText={t("farm.add.addName.error")}
                        onChange={onInputChange}
                      />
                    </DrawerForm>
                  </Container>
                </VerticalCenterContainer>
              </DialogContent>
              <DialogActions>
                {userFarms.length > 0 && (
                  <Button
                    component={Link}
                    to={`/farm/${currentFarmGuid}`}
                    color="secondary"
                  >
                    {t("edit.button.cancel")}
                  </Button>
                )}
                <Button
                  color="primary"
                  onClick={(e) => {
                    if (farmName === "") {
                      e.preventDefault();
                      setFarmNameError(true);
                    } else {
                      history.push(`${path}/find-location`);
                    }
                  }}
                >
                  {t("edit.button.next")}
                </Button>
              </DialogActions>
            </FullScreenDialog>
          </PrivateRoute>
          <PrivateRoute path={`${path}/find-location`}>
            <Dialog
              open={true}
              fullScreen
              style={{
                pointerEvents: "none",
                background: "transparent",
                backgroundColor: "transparent",
              }}
              PaperProps={{
                style: {
                  backgroundColor: "transparent",
                  background: "transparent",
                  boxShadow: "none",
                  pointerEvents: "none",
                  overflow: "visible",
                },
              }}
              BackdropProps={{
                style: {
                  backgroundColor: "transparent",
                  background: "transparent",
                  pointerEvents: "none",
                },
              }}
              hideBackdrop={true}
              sx={{
                "& .MuiDialog-paper": {
                  backgroundColor: "transparent !important",
                  background: "transparent !important",
                },
                "& .MuiDialog-container": {
                  backgroundColor: "transparent !important",
                  background: "transparent !important",
                },
              }}
            >
              <MobileTopHintContainer
                style={{
                  pointerEvents: "auto",
                  background: "transparent",
                  backgroundColor: "transparent",
                }}
              >
                <PostcodeLookupInput />
              </MobileTopHintContainer>
              <InfoSnackbar
                open={snackbarOpen}
                handleClose={handleSnackbarClose}
                message={t("farm.add.locationInstructions")}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "calc(64px + env(safe-area-inset-bottom))",
                  left: 0,
                  right: 0,
                  pointerEvents: "auto",
                  backgroundColor: "white",
                  boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                  borderTop: "1px solid #e0e0e0",
                  padding: "16px",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    color="secondary"
                    onClick={() => history.push("enter-name")}
                  >
                    {t("edit.button.back")}
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleSave}
                    disabled={isLoading}
                  >
                    {t("edit.button.save")}
                  </Button>
                </div>
              </div>
            </Dialog>
          </PrivateRoute>
        </Switch>
      </>
    );
  } else {
    return (
      <>
        <Container style={{ whiteSpace: "normal", paddingTop: "20px" }}>
          <Typography variant="h6" paragraph>
            {t("farm.add.addName.desktop")}
          </Typography>
          <TextField
            variant="standard"
            required
            value={farmName}
            error={farmNameError}
            helperText={t("farm.add.addName.error")}
            onChange={onInputChange}
            fullWidth
            style={{ paddingBottom: "25px" }}
            color="primary"
          />
          <PostcodeLookupInput />
          <Typography variant="body1" paragraph>
            {t("farm.add.saveLocation.desktop")}
            <span role="img" aria-label="point right">
              👉
            </span>
            {t("farm.add.saveFarm.desktop")}
          </Typography>
        </Container>
        <Container>
          {userFarms.length > 0 && (
            <CancelButton
              onClick={() => history.push(`/farm/${currentFarmGuid}`)}
              color="secondary"
            >
              {t("edit.button.cancel")}
            </CancelButton>
          )}
          <SaveButton color="primary" onClick={handleSave} disabled={isLoading}>
            {t("edit.button.save")}
          </SaveButton>
        </Container>
      </>
    );
  }
  function handleSnackbarClose() {
    setSnackbarOpen(false);
  }
};

const SaveButton = styled(Button)(
  ({ theme }) => `
        position: absolute;
        right: ${theme.spacing(3)}
    `
);

const CancelButton = styled(Button)(
  ({ theme }) => `
        position: absolute;
        right: ${theme.spacing(15)}
    `
);
